/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [TEMPLATE-FIND] HOW A PERSON NAMES ONE DUTY POINT OF A TEMPLATE, AND THE CODE ITS DECISION IS
 *  STORED UNDER — one expression of each, called by the api and the web alike.
 *
 *  A template duty point's KEY (`g1.d34`) is the frozen import's POSITION address: group (one of
 *  the booklet's zone/sector tables) 1, the 34th row PAMS read down that table, counted across
 *  pages (`templateImport.plan.ts`). It is stable only because a template is create-only, and it
 *  is never words. A person reads the booklet's own serial where one was printed — `(b)` — and
 *  otherwise `table 1, row 34`. The raw key stays in URLs and test ids.
 *
 *  ⚠️ TWO PROMISES OTHER CODE RELIES ON. A label never holds ` · ` — a decision label is
 *  `<template> · <label>`, and the library panel splits it there (`openDecisions.ts`) — and it
 *  starts with `(` only for a printed serial, which is how the panel's wording tells the two apart.
 *
 *  ⚠️ THE IMPORT KEEPS ITS OWN KEY BUILDER — it is frozen ([P7B.1] §5) — and
 *  `templateImport.plan.test.ts` holds it to `parseTemplateDutyPointKey`, the `SOURCE_NONE_CODE`
 *  precedent. A row number counts PAMS's reading of a table that may run over several pages, so
 *  every screen that names one shows the page beside it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type TemplateDutyPointPosition = { readonly table: number; readonly row: number };

/** What a duty point is named from: the import's key, and the booklet's own serial where one was printed. */
export type TemplateDutyPointNaming = { readonly key: string; readonly sl?: string | null | undefined };

const KEY = /^g([1-9]\d*)\.d([1-9]\d*)$/;

/** The booklet's serial, or `null` — an empty one is no serial, as the label has always read it. */
const serialOf = (point: TemplateDutyPointNaming): string | null =>
  point.sl === null || point.sl === undefined || point.sl === '' ? null : point.sl;

/** `g1.d34` → `{ table: 1, row: 34 }`; anything else → `null`. */
export const parseTemplateDutyPointKey = (key: string): TemplateDutyPointPosition | null => {
  const [, table, row] = KEY.exec(key) ?? [];
  return table === undefined || row === undefined ? null : { table: Number(table), row: Number(row) };
};

/** `(b)` where the booklet printed a serial; else `table 1, row 34`; a key that is not a position is itself. Total. */
export const templateDutyPointLabelOf = (point: TemplateDutyPointNaming): string => {
  const serial = serialOf(point);
  if (serial !== null) return `(${serial})`;
  const position = parseTemplateDutyPointKey(point.key);
  return position === null ? point.key : `table ${String(position.table)}, row ${String(position.row)}`;
};

/** The same, inside a sentence: `duty point (b)`, or `duty point at table 1, row 34`. */
export const templateDutyPointPhraseOf = (point: TemplateDutyPointNaming): string =>
  serialOf(point) === null && parseTemplateDutyPointKey(point.key) !== null
    ? `duty point at ${templateDutyPointLabelOf(point)}`
    : `duty point ${templateDutyPointLabelOf(point)}`;

/** Ascending rows folded into runs: `[1, 2, 3, 4, 7]` → `['1–4', '7']`. */
const runsOf = (rows: readonly number[]): string[] => {
  const runs: Array<[number, number]> = [];
  for (const row of rows) {
    const last = runs.at(-1);
    if (last !== undefined && row === last[1] + 1) last[1] = row;
    else runs.push([row, row]);
  }
  return runs.map(([start, end]) => (start === end ? String(start) : `${String(start)}–${String(end)}`));
};

/**
 * The rows a printed Total covers, as a person reads them: `table 1, rows 1–4; table 2, row 1`. Runs of
 * consecutive rows are folded, tables keep their first-seen order, and a key that is not a position is
 * listed as itself at the end. `''` for none.
 */
export const templateDutyPointRowsOf = (keys: readonly string[]): string => {
  const tables = new Map<number, Set<number>>();
  const others: string[] = [];
  for (const key of keys) {
    const position = parseTemplateDutyPointKey(key);
    if (position === null) {
      others.push(key);
      continue;
    }
    tables.set(position.table, (tables.get(position.table) ?? new Set<number>()).add(position.row));
  }
  const parts = [...tables].map(([table, rows]) => {
    const sorted = [...rows].sort((a, b) => a - b);
    return `table ${String(table)}, ${sorted.length > 1 ? 'rows' : 'row'} ${runsOf(sorted).join(', ')}`;
  });
  return [...parts, ...others].join('; ');
};

/** The code a `DUTY_POINT` decision is stored under — `t152.g1.d34`. */
export const templateDutyPointCodeOf = (templateKey: string, dutyPointKey: string): string =>
  `${templateKey}.${dutyPointKey}`;

/** A `DUTY_POINT` code's two halves, split at the FIRST `.` (a template key holds none); `null` if either is empty. */
export const parseTemplateDutyPointCode = (code: string): { templateKey: string; dutyPointKey: string } | null => {
  const at = code.indexOf('.');
  if (at <= 0 || at === code.length - 1) return null;
  return { templateKey: code.slice(0, at), dutyPointKey: code.slice(at + 1) };
};
